import React, { memo, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Button, Modal } from "antd";

//assets
import {
  ArrowLeftOutlined,
  CloseOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";

// components
import StepByStepWrapper from "mobile/components/StepByStepWrapper";
// cta notifications
import FinalScreen from "mobile/components/FinalScreen";
import NotificationForParents from "mobile/components/NotificationForParents";

// inner forms components
import {
  AbsenceTypeForm,
  SicknessForm,
  // inner medical/no-medical components
  ReasonForm,
  DurationForm,
  MediaForm,
  MediaFormPreview,
  ParentsUndertakeForm,
  ResumeForm,
  RemarkForm,
  UnjustifiedForm,
} from "mobile/components/CreateAbsence/components";

// utils
import history from "utils/history";
import useDeviceDetect from "utils/useDeviceDetect";
import { clean, getObjId } from "utils/helpers/helper.general";
import { useRefState } from "utils/hooks";
import _local from "utils/localStorageUtils";

// contexts
import { useGetUserChildren } from "engine/Contexts/User.context";

function CreateAbsence({
  absence,
  buttonText,
  collapseButton,
  hideButton,
  isOpen,
  // actions
  setOpen,
  change,
  // default
  className,
}) {
  const childClassName = classNames("create-absence-wrapper", className);
  const { isMobile } = useDeviceDetect();
  const { t } = useTranslation();

  const isEdit = !!absence && !!absence?._id && absence;
  const localUser = _local.getLocalUser();
  const contextChild = _local.getLocalChild();
  const children = useGetUserChildren()();

  const [step, stepRef, setStep] = useRefState(!!isEdit ? 4 : 1);
  const [totalStep, totalStepRef, setTotalStep] = useRefState(8);
  const [showFinal, setShowFinal] = useState(false);

  let initAbsence = {
    status: "pending",
    type: "medical",
  };
  let initStdInfo = null;

  if (localUser?.role == "parent") {
    initAbsence.student = getObjId(contextChild);
    initAbsence.parent = getObjId(localUser);
    initStdInfo = children?.find(s => getObjId(s) == getObjId(contextChild));
  }

  const [absenceInfo, absenceInfoRef, setAbsenceInfo] = useRefState(
    (!!isEdit && isEdit) || initAbsence,
  );
  const [absenceType, setAbsenceType] = useState(
    absence?.type || initAbsence?.type,
  );
  const [selectedStdObj, setSelectedStdObj] = useState(initStdInfo);

  // default form options
  let formOptions = {
    formData: absenceInfoRef?.current || initAbsence,
    selectedStudent: selectedStdObj,
    setSelectedStudent: obj => setSelectedStdObj(obj),
    isNew: !!!isEdit?._id || !!!absenceInfo?._id,
    prevUnjustified: absence?.type == "unjustified",
    isEdit: !!isEdit && isEdit,
    currentStep: stepRef?.current,
    // Step Navigation
    nextStep: () =>
      stepRef?.current != totalStepRef?.current &&
      setStep(stepRef?.current + 1),
    prevStep: () => {
      return stepRef?.current > 1 && setStep(stepRef?.current - 1);
    },
    goToStep: number => setStep(number),
    result: values => resultData(values),
  };

  // set Absence type from history states
  useEffect(() => {
    const locationStateOpen = history?.location?.state?.openAbsence;
    if (locationStateOpen) {
      setAbsenceType(locationStateOpen);
      setOpen(true);
      const dataAbs = absence || initAbsence;
      setAbsenceInfo({ ...dataAbs, type: locationStateOpen });
      setSelectedStdObj(absence?.student || null);
      //  setAbsenceType(locationStateOpen);
    }
    if (localUser?.role == "parent") {
      setSelectedStdObj(initStdInfo);
    }
  }, []);

  // reset states if child was changes
  useEffect(() => {
    if (localUser?.role == "parent") {
      const initStd = children?.find(
        s => getObjId(s) == getObjId(contextChild),
      );
      setSelectedStdObj(initStd);
      setAbsenceInfo(prev => ({
        ...prev,
        student: getObjId(contextChild),
      }));
      formOptions.selectedStudent = initStd;
    }
    if (!!isOpen) {
      setStep(1);
      setShowFinal(false);
    }
  }, [contextChild?._id]);

  // set Absence type from history states
  useEffect(() => {
    if (localUser?.role == "parent") {
      setSelectedStdObj(initStdInfo);
    }
    if (!!isOpen) {
      setStep(1);
      setShowFinal(false);
    }
  }, [isOpen]);

  /**
   * Change total amount Step by Absence Type
   */
  useEffect(() => {
    if (absence !== null && absence) {
      formOptions.formData = absence;
      setAbsenceInfo(absence);
      setAbsenceType(absence?.type);
      if (localUser?.role == "parent") {
        setSelectedStdObj(initStdInfo || absence?.student || null);
      } else {
        setSelectedStdObj(absence?.student || null);
      }
    }
  }, [absence]);

  /**
   * Change total amount Step by Absence Type
   */
  useEffect(() => {
    if (!!absenceType) {
      initTotalStep(absenceType);
    }
  }, [absenceType, isEdit]);

  const initTotalStep = useCallback(
    (absenceType, prevType) => {
      const asUnjustified = !!prevType && prevType == "unjustified" && !!isEdit?._id && absenceType != "unjustified";
      switch (absenceType) {
        case "sickness":
          if (!!isEdit?._id && localUser?.role == "parent") {
            if (!!asUnjustified) {
              setTotalStep(2);
              setStep(1);
            } else {
              if (
                isEdit?.status == "sickness-finish" &&
                isEdit?.media?.length == 0
              ) {
                setTotalStep(4);
                setStep(4);
              } else {
                setStep(1);
                setTotalStep(1);
              }
            }
          } else {
            setTotalStep(2);
            !!isEdit && setStep(2);
          }
          break;
        case "unjustified":
          setAbsenceInfo(prev => ({ ...prev, status: "unjustified" }));
          if (!!isEdit?._id && localUser?.role == "parent") {
            setTotalStep(8);
            setStep(1);
          } else {
            if (localUser?.role == "teacher") {
              setTotalStep(2);
              !!isEdit && setStep(2);
            } else {
              setTotalStep(8);
              setStep(8);
            }
          }
          break;
        case "medical":
          if (!!isEdit?._id && localUser?.role == "parent") {
            if (!!asUnjustified) {
              setTotalStep(8);
              setStep(1);
            } else {
              setTotalStep(4);
              setStep(4);
            }
          } else {
            setTotalStep(8);
            !!isEdit && setStep(8);
          }
          break;
        case "no-medical":
          if (!!isEdit?._id && localUser?.role == "parent") {
            if (!!asUnjustified) {
              setTotalStep(8);
              setStep(1);
            } else {
              setTotalStep(4);
              setStep(4);
            }
          } else {
            setTotalStep(8);
            !!isEdit && setStep(8);
          }
          break;

        default:
          setStep(!!isEdit?._id ? 8 : 1);
          setTotalStep(8);
      }
    },
    [isEdit, localUser?.role],
  );

  // Main result/save data
  const resultData = result => {
    let absenceCurrent = absenceInfo;
    const asUnjustified = absence?.type == "unjustified";
    const newObj = { ...absenceCurrent, ...result };

    if (!!result?.type) {
      setAbsenceType(result?.type);
      absence?.type == "unjustified" &&
        initTotalStep(result?.type, absence?.type);
    }

    if (newObj?.type == "sickness" && !!!isEdit) {
      newObj.status = "sick";
    }

    if (newObj?.type == "sickness" && result?.status) {
      newObj.status = result?.status;
    }

    if (
      !!asUnjustified && absence?.type != result?.type && result?.type != "unjustified"
    ) {
      newObj.status = result?.type == "sickness" ? "sick" : "pending";
      newObj.unjustified = true;
    } else if (
      (absence?.type == "unjustified" && newObj.type == "unjustified") ||
      (!absence?.type && newObj?.type == "unjustified")
    ) {
      newObj.status = "unjustified";
      newObj.unjustified = true;
    }

    setAbsenceInfo(newObj);

    if ( localUser?._id && stepRef?.current == totalStepRef?.current) {
      setShowFinal(true);
      const cleanData = clean(newObj);
      cleanData.media = (newObj?.media?.length > 0 && newObj?.media) || [];
      change(cleanData);
    }
  };

  // Update Absence status, only for 'sickness' and 'parent' user role
  const resultSicknessCallback = status => {
    let absenceCurrent = absenceInfo;

    if (absenceCurrent?.status != status) {
      const newObj = {
        ...absenceCurrent,
        status: status,
      };
      setAbsenceInfo(newObj);

      if (localUser?._id && step == totalStep) {
        setShowFinal(true);
        const cleanData = clean(newObj);
        change(cleanData);
      }
    } else {
      if (localUser?._id && step == totalStep) {
        setShowFinal(true);
      }
    }

    closeModal();
  };

  const closeModal = () => {
    setShowFinal(false);
    setOpen(false);
    // ----
    setAbsenceInfo(initAbsence);
    setStep(1);
    setAbsenceType(initAbsence?.type);
    initTotalStep(initAbsence?.type);
    setSelectedStdObj(null);
  };
  const openModal = () => {
    setStep(1);
    setShowFinal(false);
    setOpen(true);
  };

  const studentName = useCallback((stdObj, crop) => {
    const stdNameStr = stdObj?.description
      ? `${stdObj?.description?.lastname} ${
          crop && stdObj?.description?.firstname
            ? stdObj?.description?.firstname.substring(0, 1) + "."
            : stdObj?.description?.firstname
        }`
      : "";
    return (
      stdNameStr && <span className="student-name-title">{stdNameStr}</span>
    );
  }, []);

  const finalNotification =
    absenceType == "sickness" ? (
      <FinalScreen
        title={t("absences.finalScreenTitleParent")}
        continueAction={closeModal}
      >
        {t("absences.finalScreenDescriptionParent")}
      </FinalScreen>
    ) : (
      <>
        {!!isEdit && localUser?.role == "teacher" ? (
          <FinalScreen
            title={t("absences.finalScreenTitleTeacher")}
            content={t("absences.finalScreenDescriptionTeacher")}
            continueAction={closeModal}
          />
        ) : (
          <FinalScreen continueAction={closeModal} />
        )}
      </>
    );

  const medicalSteps = stepNumber => {
    switch (stepNumber) {
      case 1:
        return (
          <StepByStepWrapper
            number={1}
            isActive={stepNumber == 1}
            header={false} // `Étape ${1} sur ${4}`
            title={t("absences.absencesReason")}
          >
            <AbsenceTypeForm {...formOptions} role={localUser?.role} />
          </StepByStepWrapper>
        );
      case 2:
        return (
          <StepByStepWrapper
            number={2}
            isActive={stepNumber == 2}
            header={false} // `Étape ${1} sur ${4}`
            title={t("absences.absencesReason")}
          >
            <ReasonForm {...formOptions} type={absenceType} />
          </StepByStepWrapper>
        );
      case 3:
        return (
          <StepByStepWrapper
            number={3}
            isActive={stepNumber == 3}
            header={false} //`Étape ${2} sur ${4}`
            title={t("absences.absenceDuration")}
          >
            <DurationForm {...formOptions} role={localUser?.role} />
          </StepByStepWrapper>
        );
      case 4:
        return (
          <StepByStepWrapper
            number={4}
            isActive={stepNumber == 4}
            header={false} // `Étape ${3} sur ${4}`
            title={t("absences.absenceJustification")}
          >
            <MediaForm {...formOptions} role={localUser?.role} />
          </StepByStepWrapper>
        );
      case 5:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={5}
                isActive={stepNumber == 5}
                header={false} // `Étape ${3} sur ${4}`
                title={t("absences.absenceJustification")}
              >
                <MediaFormPreview {...formOptions} role={localUser?.role} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 6:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={6}
                isActive={stepNumber == 6}
                header={false} // `Étape ${4} sur ${4}`
                title={t("absences.displacement")}
              >
                <ParentsUndertakeForm {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        ); 
      case 7:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={7}
                isActive={stepNumber == 7}
                header={false} // `Étape ${4} sur ${4}`
                title={t("common.note")}
              >
                <RemarkForm {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 8:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={8}
                isActive={stepNumber == 8}
                header={
                  selectedStdObj ? t("absences.applicationSummary") : t("absences.summary")
                }
                title={
                  studentName(selectedStdObj) || t("absences.applicationSummary")
                }
              >
                <ResumeForm {...formOptions} role={localUser?.role} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
    }
  };

  const medicalStepsParentEdit = stepNumber => {
    switch (stepNumber) {
      case 1:
        return (
          <StepByStepWrapper
            number={1}
            isActive={stepNumber == 1}
            header={false} //`Étape ${3} sur ${4}`
            title={t("absences.absenceJustification")}
          >
            <MediaForm {...formOptions} />
          </StepByStepWrapper>
        );
      case 2:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={2}
                isActive={stepNumber == 2}
                header={false} // `Étape ${3} sur ${4}`
                title={t("absences.absenceJustification")}
              >
                <MediaFormPreview {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 3:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={3}
                isActive={stepNumber == 3}
                header={false} // `Étape ${4} sur ${4}`
                title={t("common.note")}
              >
                <RemarkForm {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 4:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={4}
                isActive={stepNumber == 4}
                header={
                  selectedStdObj ? t("absences.applicationSummary") : t("absences.summary")
                }
                title={
                  studentName(selectedStdObj) || t("absences.applicationSummary")
                }
              >
                <ResumeForm {...formOptions} role={localUser?.role} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
    }
  };
  const sicknessStepsParentEdit = (elem, stepNumber) => {
    if (
      elem?.media?.length > 0 &&
      elem?.status == "sickness-finish" &&
      totalStep <= 1
    ) {
      return (
        <>
          {!showFinal && (
            <StepByStepWrapper
              number={1}
              isActive={stepNumber == 1}
              header={selectedStdObj ? t("absences.applicationSummary") : t("absences.summary")}
              title={
                studentName(selectedStdObj) || t("absences.applicationSummary")
              }
            >
              <ResumeForm {...formOptions} role={localUser?.role} />
            </StepByStepWrapper>
          )}
          {/* // Last Step Notificationswindows */}
          {showFinal && finalNotification}
        </>
      );
    } else {
      switch (stepNumber) {
        case 1:
          return (
            <StepByStepWrapper
              number={1}
              isActive={stepNumber == 1}
              header={false} // `Étape ${1} sur ${3}`
              title={t("absences.absenceJustification")}
            >
              <MediaForm
                {...formOptions}
                currentStep={stepNumber}
                prevStep={() => {
                  setStep(3);
                }}
              />
            </StepByStepWrapper>
          );
        case 2:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={2}
                  isActive={stepNumber == 2}
                  header={false} // `Étape ${2} sur ${3}`
                  title={t("absences.absenceJustification")}
                >
                  <MediaFormPreview {...formOptions} />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
        case 3:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={3}
                  isActive={stepNumber == 3}
                  header={false} // `Étape ${4} sur ${4}`
                  title={t("common.note")}
                >
                  <RemarkForm {...formOptions} />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
        case 4:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={4}
                  isActive={stepNumber == 4}
                  header={
                    selectedStdObj ? t("absences.applicationSummary") : t("absences.summary")
                  }
                  title={
                    studentName(selectedStdObj) || t("absences.applicationSummary")
                  }
                >
                  <ResumeForm {...formOptions} role={localUser?.role} />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
      }
    }
  };
  const showSicknessModalForParent =
    !!isEdit?._id &&
    localUser?.role == "parent" && absence?.type != "unjustified" &&
    (absenceType == "sickness" && absenceInfo?.status == "sick");

  return (
    <div className={childClassName}>
      {!hideButton && (
        <Button
          className={`create-absence-wrapper__modal-btn ${
            collapseButton ? "collapsed-btn" : ""
          }`}
          onClick={openModal}
        >
          <PlusOutlined />
          {!!buttonText && !collapseButton ? (
            <span className="text">{t(buttonText)}</span>
          ) : (
            ""
          )}
        </Button>
      )}

      {!!showSicknessModalForParent && (
        <NotificationForParents
          visibility={isOpen}
          okHandle={() => resultSicknessCallback("sickness-finish")}
          closeHandle={() => resultSicknessCallback("sick")}
          cancelHandle={() => resultSicknessCallback("sick")}
          child={selectedStdObj}
        />
      )}

      {!showSicknessModalForParent && (
        <>
          {/* Create Absence Modal */}
          <Modal
            className="create-absence"
            title={
              <>
                {isMobile && (
                  <>
                    {/* <MoreOutlined className="create-absence__mobile-menu" /> */}
                    <div className="create-absence__mobile-menu" />
                  </>
                )}
                <div className="modal-title">
                  {t("absences.absenceRequest")}
                  {selectedStdObj && ": "}
                  {studentName(selectedStdObj, true)}
                </div>
              </>
            }
            open={isOpen}
            closeIcon={
              <span className="create-absence__close" onClick={closeModal}>
                {!isMobile ? (
                  <>
                    <CloseOutlined /> {t("common.close")}
                  </>
                ) : (
                  <ArrowLeftOutlined />
                )}
              </span>
            }
            onCancel={closeModal}
            footer={[]}
          >
            {localUser?.role == "parent" && (
              <>
                {/* Only on edit */}
                {!!isEdit && absence?.type != "unjustified" && (
                  <>
                    {absenceType != "sickness" && absenceType != "unjustified" &&
                      medicalStepsParentEdit(step)}
                    {absenceType == "sickness" &&
                      absenceInfo?.status == "sickness-finish" &&
                      sicknessStepsParentEdit(absenceInfo, step)}
                  </>
                )}

                {/* Only on edit (unjustified) */}
                {!!isEdit?._id && (
                  (absence?.type == "unjustified" && absenceType != "sickness" && absenceType != "unjustified") ||
                  (absenceType == "unjustified")
                ) && (
                    <>{medicalSteps(step)}</>
                  )}

                {/* Only on edit (unjustified) as sickness */}
                {(!!isEdit?._id && (
                  (absence?.type == "unjustified" && absenceType == "sickness") 
                )) && (
                    <>
                    {!showFinal && (
                      <>
                        <StepByStepWrapper
                          number={1}
                          isActive={step == 1}
                          header={false} // `Étape ${1} sur ${totalStep}`
                          title={t("absences.absencesReason")}
                        >
                          <AbsenceTypeForm
                            {...formOptions}
                            role={localUser?.role}
                          />
                        </StepByStepWrapper>
                        <StepByStepWrapper
                          number={2}
                          isActive={step == 2}
                          header={t("absences.absenceRequest")}
                          title={t("absences.siknessAbsence")}
                        >
                          <SicknessForm {...formOptions} />
                        </StepByStepWrapper>
                      </>
                    )}
                    {showFinal && finalNotification}
                  </>
                )}

                {/* Only on new */}
                {!!!isEdit && (
                  <>
                    {absenceType == "sickness" && (
                      <>
                        {!showFinal && (
                          <>
                            <StepByStepWrapper
                              number={1}
                              isActive={step == 1}
                              header={false} // `Étape ${1} sur ${totalStep}`
                              title={t("absences.absencesReason")}
                            >
                              <AbsenceTypeForm
                                {...formOptions}
                                role={localUser?.role}
                              />
                            </StepByStepWrapper>
                            <StepByStepWrapper
                              number={2}
                              isActive={step == 2}
                              header={t("absences.absenceRequest")}
                              title={t("absences.siknessAbsence")}
                            >
                              <SicknessForm {...formOptions} />
                            </StepByStepWrapper>
                          </>
                        )}
                        {showFinal && finalNotification}
                      </>
                    )}

                    {absenceType != "sickness" && medicalSteps(step)}
                  </>
                )}
              </>
            )}

            {localUser?.role == "teacher" && (
              <>
                {absenceType == "sickness" && (
                  <>
                    {!showFinal && (
                      <>
                        <StepByStepWrapper
                          number={1}
                          isActive={step == 1}
                          header={false} // `Étape ${1} sur ${totalStep}`
                          title={t("absences.absencesReason")}
                        >
                          <AbsenceTypeForm
                            {...formOptions}
                            role={localUser?.role}
                          />
                        </StepByStepWrapper>
                        <StepByStepWrapper
                          number={2}
                          isActive={step == 2}
                          header={t("absences.absenceRequest")}
                          title={t("absences.siknessAbsence")}
                        >
                          <SicknessForm {...formOptions} />
                        </StepByStepWrapper>
                      </>
                    )}
                    {showFinal && finalNotification}
                  </>
                )}

                {absenceType == "unjustified" && (
                  <>
                    {!showFinal && (
                      <>
                        <StepByStepWrapper
                          number={1}
                          isActive={step == 1}
                          header={false} // `Étape ${1} sur ${totalStep}`
                          title={t("absences.absencesReason")}
                        >
                          <AbsenceTypeForm
                            {...formOptions}
                            role={localUser?.role}
                          />
                        </StepByStepWrapper>
                        <StepByStepWrapper
                          number={2}
                          isActive={step == 2}
                          header={t("absences.absenceRequest")}
                          title={t("absences.unjustifiedDuration")}
                        >
                          <UnjustifiedForm {...formOptions} />
                        </StepByStepWrapper>
                      </>
                    )}
                    {showFinal && finalNotification}
                  </>
                )}

                {absenceType != "sickness" &&
                  absenceType != "unjustified" &&
                  medicalSteps(step)}
              </>
            )}
          </Modal>
        </>
      )}
    </div>
  );
}

CreateAbsence.defaultProps = {
  isOpen: false,
  buttonText: "absences.newAbsence",
  collapseButton: false,
  hideButton: false,
};

CreateAbsence.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  absence: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  change: PropTypes.func.isRequired,
};

export default memo(CreateAbsence);
